import {
  PRINT_FONT_LABELS,
  PRINT_PAGE_SIZE_LABELS,
  type PrintPageSize,
  printPageSizeSchema,
  type PrintTemplateView,
  type ReportView,
} from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { Segmented } from '#client/components/ui/Segmented';
import { Select } from '#client/components/ui/Select';
import type { PrintJobOptions } from '#client/lib/endpoints';
import {
  type EffectivePrintOptions,
  effectivePrintOptions,
  toggleReportSelection,
} from './print-options.logic';

interface PrintOptionsProps {
  templates: PrintTemplateView[];
  /** The template these options are read against — the chosen one, or the default. */
  active: PrintTemplateView | undefined;
  reports: ReportView[];
  /** `undefined` means every report. */
  selected: number[] | undefined;
  onSelectedChange: (ids: number[] | undefined) => void;
  options: PrintJobOptions;
  onChange: (patch: PrintJobOptions) => void;
}

/**
 * What one press of Print may change, without editing the lab's template.
 *
 * Every control here starts from the active template and says so: the toggles
 * show the template's own answer until somebody changes one, at which point the
 * change applies to this sheet and nothing else. That separation is the whole
 * reason the template is not simply a pile of settings on this screen — the
 * receptionist who needs A5 because that is what the tray holds should not
 * thereby decide what the lab prints on tomorrow.
 */
export function PrintOptions({
  templates,
  active,
  reports,
  selected,
  onSelectedChange,
  options,
  onChange,
}: PrintOptionsProps) {
  const document = active?.document;
  const effective = effectivePrintOptions(options, document);

  return (
    <div className="c-print-options">
      <TemplateCard
        templates={templates}
        active={active}
        onTemplateChange={(templateId) => onChange({ template_id: templateId })}
      />

      {reports.length > 1 && (
        <TestsCard reports={reports} selected={selected} onSelectedChange={onSelectedChange} />
      )}

      <PaperCard
        pageSize={effective.pageSize}
        orientation={effective.orientation}
        onChange={onChange}
      />

      <IncludeCard effective={effective} onChange={onChange} />
    </div>
  );
}

function TemplateCard({
  templates,
  active,
  onTemplateChange,
}: {
  templates: readonly PrintTemplateView[];
  active: PrintTemplateView | undefined;
  onTemplateChange: (templateId: number | undefined) => void;
}) {
  const document = active?.document;
  return (
    <Card title="Template">
      <Select
        label="Letterhead"
        hideLabel
        value={String(active?.id ?? '')}
        onChange={(choice) => onTemplateChange(choice ? Number(choice) : undefined)}
        options={templates.map((template) => ({
          value: String(template.id),
          label: template.is_default ? `${template.name} (default)` : template.name,
        }))}
      />
      {document && (
        <p className="c-print-options__hint">
          {PRINT_FONT_LABELS[document.theme.font].split(' — ')[0]} ·{' '}
          {document.body.columns.filter((column) => column.enabled).length} columns
          {document.body.test_flow === 'new_page' ? ' · one test per page' : ''}
        </p>
      )}
    </Card>
  );
}

function TestsCard({
  reports,
  selected,
  onSelectedChange,
}: Pick<PrintOptionsProps, 'reports' | 'selected' | 'onSelectedChange'>) {
  return (
    <Card title="Tests" subtitle={`${(selected ?? reports).length} of ${reports.length}`}>
      <div className="c-print-options__list">
        {reports.map((report) => (
          <Checkbox
            key={report.id}
            label={report.name}
            checked={selected === undefined || selected.includes(report.id)}
            onChange={(event) =>
              onSelectedChange(
                toggleReportSelection(
                  selected,
                  reports.map((each) => each.id),
                  { reportId: report.id, isIncluded: event.target.checked },
                ),
              )
            }
          />
        ))}
      </div>
    </Card>
  );
}

function PaperCard({
  pageSize,
  orientation,
  onChange,
}: {
  pageSize: string;
  orientation: EffectivePrintOptions['orientation'];
  onChange: (patch: PrintJobOptions) => void;
}) {
  return (
    <Card title="Paper">
      <Select
        label="Page size"
        value={pageSize}
        onChange={(choice) => onChange({ page_size: choice })}
        options={printPageSizeSchema.options.map((size: PrintPageSize) => ({
          value: size,
          label: PRINT_PAGE_SIZE_LABELS[size],
        }))}
      />
      <Segmented
        label="Orientation"
        className="c-print-options__orientation"
        value={orientation}
        onChange={(value) => onChange({ orientation: value })}
        options={[
          { value: 'portrait', label: 'Portrait' },
          { value: 'landscape', label: 'Landscape' },
        ]}
      />
    </Card>
  );
}

function IncludeCard({
  effective,
  onChange,
}: {
  effective: EffectivePrintOptions;
  onChange: (patch: PrintJobOptions) => void;
}) {
  return (
    <Card title="Include">
      <div className="c-print-options__list">
        <Checkbox
          label="Letterhead"
          checked={effective.header}
          onChange={(event) => onChange({ header: event.target.checked })}
        />
        <Checkbox
          label="Footer"
          checked={effective.footer}
          onChange={(event) => onChange({ footer: event.target.checked })}
        />
        <Checkbox
          label="Signatories"
          checked={effective.signatures}
          onChange={(event) => onChange({ signatures: event.target.checked })}
        />
        <Checkbox
          label="Previous results"
          checked={effective.history}
          onChange={(event) => onChange({ history: event.target.checked })}
        />
        <Checkbox
          label="Watermark"
          checked={effective.watermark}
          onChange={(event) => onChange({ watermark: event.target.checked })}
        />
      </div>
      <p className="c-print-options__hint">
        Turning the letterhead or footer off leaves the blank space the template sets — useful when
        printing onto pre-printed stationery. Turning the signatories off leaves room to sign by
        hand. Previous results add a column per earlier visit, as the template sets them up.
      </p>
    </Card>
  );
}
